import { z } from 'zod';
import { amountMinorSchema, categoryFilterSchema } from './catalog.js';

/**
 * "Pull new products" (spec §07, docs/PRODUCT_SYNC.md): a store adds catalog products it does
 * not have yet. Sync only ever adds; it never changes a product the store already has. Enum
 * values are shared with the database enums; a test checks that they match exactly.
 */

/**
 * - PENDING: waiting for the worker
 * - RUNNING: the worker is adding the products (or retrying after an error)
 * - SUCCEEDED: done
 * - FAILED: out of automatic retries; the store can simply pull again (nothing is added twice)
 */
export const CATALOG_SYNC_RUN_STATUSES = ['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED'] as const;
export const catalogSyncRunStatusSchema = z.enum(CATALOG_SYNC_RUN_STATUSES);
export type CatalogSyncRunStatus = z.infer<typeof catalogSyncRunStatusSchema>;

/**
 * What the pull adds: the products the store ticked, every product it does not have yet, or
 * (NEW) the products that entered the catalog since the store follows it: the platform pulls
 * these by itself after every catalog update, with no action by the store (adr/0042).
 */
export const CATALOG_SYNC_SELECTIONS = ['SELECTED', 'ALL', 'NEW'] as const;
export const catalogSyncSelectionSchema = z.enum(CATALOG_SYNC_SELECTIONS);
export type CatalogSyncSelection = z.infer<typeof catalogSyncSelectionSchema>;

/**
 * Why a pull stopped, stored as a code (never an error message, which could carry data).
 * - UNEXPECTED: a step threw; the worker log has the details under the tenant id
 * - STALLED: the worker kept dying on this run until the attempt limit
 */
export const CATALOG_SYNC_ERROR_CODES = ['UNEXPECTED', 'STALLED'] as const;
export const catalogSyncErrorCodeSchema = z.enum(CATALOG_SYNC_ERROR_CODES);
export type CatalogSyncErrorCode = z.infer<typeof catalogSyncErrorCodeSchema>;

/** Products one pull may name; "everything new" has no limit (the worker copies in batches). */
export const MAX_SYNC_SELECTION = 500;

export const catalogSyncRunSchema = z.object({
  id: z.uuid(),
  status: catalogSyncRunStatusSchema,
  selection: catalogSyncSelectionSchema,
  /** Products asked for when the pull was requested. */
  productsRequested: z.number().int().min(0),
  /** Products added so far. Fewer than requested when some left the catalog meanwhile. */
  productsAdded: z.number().int().min(0),
  /** Catalog categories the store did not have yet. */
  categoriesAdded: z.number().int().min(0),
  errorCode: catalogSyncErrorCodeSchema.nullable(),
  createdAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
});
export type CatalogSyncRun = z.infer<typeof catalogSyncRunSchema>;

export const catalogSyncSummarySchema = z.object({
  /** Catalog products the store does not have (never had: a removed one is not offered again). */
  candidates: z.number().int().min(0),
  /** Of those, the ones added to the catalog since the store's last pull. */
  newSinceLastPull: z.number().int().min(0),
  /** The store's products that the catalog no longer carries; the store decides what to do. */
  discontinued: z.number().int().min(0),
  /** Catalog products the store removed (ARCHIVED). They can be restored from the product list. */
  removed: z.number().int().min(0),
  /** When the last successful pull finished. */
  lastPulledAt: z.iso.datetime().nullable(),
  /** A pull that is waiting or running; no other can start until it ends. */
  activeRun: catalogSyncRunSchema.nullable(),
  /** The last pull that ended. */
  lastRun: catalogSyncRunSchema.nullable(),
});
export type CatalogSyncSummary = z.infer<typeof catalogSyncSummarySchema>;

/**
 * A catalog product the store can add. Facts only: Avrahami's marketing text is never shown to a
 * store. `id` is the catalog product's id (what a pull names).
 */
export const catalogSyncCandidateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sku: z.string(),
  model: z.string().nullable(),
  manufacturer: z.string().nullable(),
  /** The names of the catalog categories it sits in. */
  categories: z.array(z.string()),
  /** What the product costs the store: the price on the original site, VAT included. */
  costMinor: amountMinorSchema.nullable(),
  /** The sale price the store's copy starts with (the cost and the default profit on top). */
  startPriceMinor: amountMinorSchema.nullable(),
  /** The first image's file name under `/v1/tenant/catalog-sync/media/<file>`; null without one. */
  image: z.string().nullable(),
  /** When the product entered the catalog. */
  addedAt: z.iso.datetime(),
  /** Added to the catalog since the store's last pull. */
  isNew: z.boolean(),
});
export type CatalogSyncCandidate = z.infer<typeof catalogSyncCandidateSchema>;

/** Only products added to the catalog since the store's last pull. */
const onlyNewSchema = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true')
  .optional();

export const catalogSyncCandidateQuerySchema = z.object({
  /** Part of the name, SKU, model or barcode. */
  q: z.string().trim().min(1).max(100).optional(),
  onlyNew: onlyNewSchema,
  /** Candidates placed directly in this category of the catalog. */
  categoryId: categoryFilterSchema.optional(),
});
export type CatalogSyncCandidateQuery = z.infer<typeof catalogSyncCandidateQuerySchema>;

/** What the catalog's categories are counted under for the candidates (`productCategoryGroups`). */
export const catalogSyncGroupsQuerySchema = z.object({ onlyNew: onlyNewSchema });
export type CatalogSyncGroupsQuery = z.infer<typeof catalogSyncGroupsQuerySchema>;

export const startCatalogSyncRequestSchema = z.discriminatedUnion('selection', [
  z.object({ selection: z.literal('ALL') }),
  z.object({
    selection: z.literal('SELECTED'),
    masterProductIds: z
      .array(z.uuid())
      .min(1)
      .max(MAX_SYNC_SELECTION)
      .refine((ids) => new Set(ids).size === ids.length, 'duplicate product'),
  }),
]);
export type StartCatalogSyncRequest = z.infer<typeof startCatalogSyncRequestSchema>;

/** The worker job: run (or resume) the waiting pull of one tenant. */
export const catalogSyncJobSchema = z.object({ tenantId: z.uuid() });
export type CatalogSyncJob = z.infer<typeof catalogSyncJobSchema>;
